import {
    ExpandMore,
    HelpOutline,
    Search
} from '@mui/icons-material';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    alpha,
    Box,
    Chip,
    Container,
    InputAdornment,
    styled,
    TextField,
    Typography,
    useTheme
} from '@mui/material';
import { motion, Variants } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { getLocalizationFromDescriptor, getTextFromDescriptor } from '../../../../Helpers/TextDescriptorUtils';
import { getOrDefault } from '../../../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../../../Themes';
import { FAQCategory, ModernFAQProps } from './types';

/**
 * ModernFAQ Component
 * 
 * A beautiful, theme-aware FAQ section with:
 * - Smooth accordion animations
 * - Optional search functionality
 * - Category filtering
 * - Glassmorphic design
 * - Mobile responsive
 */

// Styled accordion with glassmorphism
const StyledAccordion = styled(Accordion)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(255, 255, 255, 0.8)',
    backdropFilter: 'blur(20px)',
    border: theme.palette.mode === 'dark'
        ? '1px solid rgba(255, 255, 255, 0.1)'
        : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: '16px !important',
    marginBottom: '16px',
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 24px rgba(0, 0, 0, 0.4)'
        : '0 4px 24px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    overflow: 'hidden',

    '&:before': {
        display: 'none',
    },

    '&.Mui-expanded': {
        margin: '0 0 16px 0',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
    },

    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 8px 32px rgba(0, 0, 0, 0.5)'
            : '0 8px 32px rgba(0, 0, 0, 0.1)',
    }
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    padding: '20px 24px',
    minHeight: 'auto',

    '&.Mui-expanded': {
        minHeight: 'auto',
    },

    '& .MuiAccordionSummary-content': {
        margin: '8px 0',

        '&.Mui-expanded': {
            margin: '8px 0',
        }
    },

    '& .MuiAccordionSummary-expandIconWrapper': {
        color: theme.palette.primary.main,
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',

        '&.Mui-expanded': {
            transform: 'rotate(180deg)',
        }
    }
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
    padding: '0 24px 24px 24px',
    color: theme.palette.text.secondary,
    lineHeight: 1.8,
}));

const ModernFAQ: React.FC<ModernFAQProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const { localizationProvider } = useMicrospecThemeLanguageContext();

    // Component configuration
    const componentSpec = getOrDefault(spec, {});
    const backgroundColor = getOrDefault(
        componentSpec.backgroundColor,
        theme.palette.background.default
    );
    const accentColor = getOrDefault(
        componentSpec.accentColor,
        theme.palette.primary.main
    );

    const title = localizationProvider.translateByType(getLocalizationFromDescriptor(componentSpec.title, {
        type: 'Internal',
        text: 'faqTitle'
    }));
    const subtitle = localizationProvider.translateByType(getLocalizationFromDescriptor(
        componentSpec.subtitle,
        {
            type: 'Internal',
            text: 'faqSubtitle'
        }
    ));



    const titleColor = getOrDefault(componentSpec.titleColor, theme.palette.text.primary);
    const subtitleColor = getOrDefault(
        componentSpec.subtitleColor,
        theme.palette.text.secondary
    );

    const faqs = getOrDefault(componentSpec.faqs, []);
    const categories = getOrDefault(componentSpec.categories, []);
    const enableSearch = getOrDefault(componentSpec.enableSearch, true);
    const searchPlaceholder = localizationProvider.translateByType(getLocalizationFromDescriptor(
        componentSpec.searchPlaceholder,
        {
            type: 'Internal',
            text: 'faqSearchPlaceholder'
        }
    ));
    const defaultExpanded = getOrDefault(componentSpec.defaultExpanded, false);
    const allowMultipleExpanded = getOrDefault(componentSpec.allowMultipleExpanded, true);
    const limitAll: number | undefined = componentSpec.limitAll;

    // State
    const [expanded, setExpanded] = useState<string | false>(defaultExpanded ? 'faq-0' : false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

    // Handle accordion change
    const handleAccordionChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        if (allowMultipleExpanded) {
            setExpanded(isExpanded ? panel : false);
        } else {
            setExpanded(isExpanded ? panel : false);
        }
    };

    // Handle category change - reset expanded state when category changes
    const handleCategoryChange = (categoryId: string | null) => {
        setSelectedCategory(categoryId);
        setExpanded(false); // Close all accordions when category changes
    };

    // Filter FAQs based on search and category
    const filteredFAQs = useMemo(() => {
        let filtered = [...faqs];

        // Filter by category
        if (selectedCategory) {
            filtered = filtered.filter((faq) => faq.category === selectedCategory);
        }

        // Filter by search query
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter((faq) => {
                const question = getTextFromDescriptor(faq.question, '').toLowerCase();
                const answer = getTextFromDescriptor(faq.answer, '').toLowerCase();
                return question.includes(query) || answer.includes(query);
            });
        }

        // Apply limitAll when no category is active and no search is running
        if (!selectedCategory && !searchQuery.trim() && limitAll && limitAll > 0) {
            filtered = filtered.slice(0, limitAll);
        }

        return filtered;
    }, [faqs, searchQuery, selectedCategory, limitAll]);

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1,
                delayChildren: 0.2
            }
        }
    };

    const itemVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 20
        },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 15
            }
        }
    };

    return (
        <Box
            id={spec.id}
            component="section"
            sx={{
                py: { xs: 8, md: 12 },
                background: backgroundColor,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Animated background orbs */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '10%',
                    left: '-5%',
                    width: 500,
                    height: 500,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.12)}, transparent)`,
                    filter: 'blur(80px)',
                    animation: 'float-slow 25s ease-in-out infinite',
                    '@keyframes float-slow': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(80px, 80px) scale(1.1)' }
                    }
                }}
            />
            <Box
                sx={{
                    position: 'absolute',
                    bottom: '10%',
                    right: '-5%',
                    width: 400,
                    height: 400,
                    borderRadius: '50%',
                    background: `radial-gradient(circle, ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.1)}, transparent)`,
                    filter: 'blur(70px)',
                    animation: 'float-reverse 20s ease-in-out infinite',
                    '@keyframes float-reverse': {
                        '0%, 100%': { transform: 'translate(0, 0) scale(1)' },
                        '50%': { transform: 'translate(-60px, -60px) scale(1.12)' }
                    }
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                >
                    <Box sx={{ textAlign: 'center', mb: 6 }}>
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 64,
                                height: 64,
                                borderRadius: '50%',
                                background: theme.palette.mode === 'dark' ? "#f0f0f0" : `linear-gradient(135deg, ${alpha(accentColor, 0.2)}, ${alpha(accentColor, 0.05)})`,
                                border: `2px solid ${alpha(theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor, 0.3)}`,
                                mb: 3,
                                mx: 'auto'
                            }}
                        >
                            <HelpOutline sx={{ fontSize: 32, color: accentColor }} />
                        </Box>

                        <Typography
                            variant="h2"
                            sx={{
                                color: titleColor,
                                fontWeight: 800,
                                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                                lineHeight: 1.2,
                                mb: 2,
                                background: theme.palette.mode === 'dark'
                                    ? `#f0f0f0`
                                    : `linear-gradient(135deg, ${titleColor}, ${accentColor})`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            {title}
                        </Typography>

                        <Typography
                            variant="h6"
                            sx={{
                                color: subtitleColor,
                                fontWeight: 400,
                                fontSize: { xs: '1rem', md: '1.25rem' },
                                maxWidth: '700px',
                                mx: 'auto',
                                lineHeight: 1.6
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </Box>
                </motion.div>

                {/* Search Bar */}
                {enableSearch && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.2 }}
                    >
                        <Box sx={{ mb: 4, maxWidth: 600, mx: 'auto' }}>
                            <TextField
                                fullWidth
                                placeholder={searchPlaceholder}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Search sx={{ color: theme.palette.mode === 'dark' ? "#f0f0f0" : accentColor }} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        background: theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(255, 255, 255, 0.9)',
                                        backdropFilter: 'blur(20px)',
                                        border: theme.palette.mode === 'dark'
                                            ? '1px solid rgba(255, 255, 255, 0.1)'
                                            : '1px solid rgba(0, 0, 0, 0.08)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            borderColor: alpha(accentColor, 0.5),
                                        },
                                        '&.Mui-focused': {
                                            borderColor: accentColor,
                                            boxShadow: `0 0 0 3px ${alpha(accentColor, 0.1)}`,
                                        }
                                    }
                                }}
                            />
                        </Box>
                    </motion.div>
                )}

                {/* Category Filters */}
                {categories.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.3 }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1.5,
                                justifyContent: 'center',
                                mb: 5
                            }}
                        >
                            <Chip
                                label={
                                    localizationProvider.translateByType({
                                        type: 'Internal',
                                        text: 'all',
                                    })
                                }
                                onClick={() => handleCategoryChange(null)}
                                sx={{
                                    background: !selectedCategory
                                        ? `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.8)})`
                                        : theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.05)'
                                            : 'rgba(0, 0, 0, 0.05)',
                                    color: !selectedCategory
                                        ? theme.palette.common.white
                                        : theme.palette.text.primary,
                                    fontWeight: 600,
                                    fontSize: '0.95rem',
                                    px: 1,
                                    py: 2.5,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: `0 4px 12px ${alpha(accentColor, 0.3)}`,
                                    }
                                }}
                            />
                            {categories.map((category: FAQCategory) => (
                                <Chip
                                    key={category.id}
                                    label={`${category.icon || ''} ${getTextFromDescriptor(category.name, '')}`}
                                    onClick={() => handleCategoryChange(category.id)}
                                    sx={{
                                        background: selectedCategory === category.id
                                            ? `linear-gradient(135deg, ${accentColor}, ${alpha(accentColor, 0.8)})`
                                            : theme.palette.mode === 'dark'
                                                ? 'rgba(255, 255, 255, 0.05)'
                                                : 'rgba(0, 0, 0, 0.05)',
                                        color: selectedCategory === category.id
                                            ? theme.palette.common.white
                                            : theme.palette.text.primary,
                                        fontWeight: 600,
                                        fontSize: '0.95rem',
                                        px: 1,
                                        py: 2.5,
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            transform: 'translateY(-2px)',
                                            boxShadow: `0 4px 12px ${alpha(accentColor, 0.3)}`,
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </motion.div>
                )}

                {/* FAQ Accordions */}
                <motion.div
                    variants={containerVariants}
                    // initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                >
                    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
                        {filteredFAQs.length > 0 ? (
                            filteredFAQs.map((faq, idx) => {
                                const panelId = `faq-${idx}`;
                                const question = getTextFromDescriptor(faq.question, '');
                                const answer = getTextFromDescriptor(faq.answer, '');

                                return (
                                    <motion.div key={panelId} variants={itemVariants}>
                                        <StyledAccordion
                                            expanded={expanded === panelId}
                                            onChange={handleAccordionChange(panelId)}
                                        >
                                            <StyledAccordionSummary
                                                expandIcon={<ExpandMore />}
                                                aria-controls={`${panelId}-content`}
                                                id={`${panelId}-header`}
                                            >
                                                <Typography
                                                    sx={{
                                                        fontWeight: 600,
                                                        fontSize: { xs: '1rem', md: '1.1rem' },
                                                        color: theme.palette.text.primary,
                                                        pr: 2
                                                    }}
                                                >
                                                    {question}
                                                </Typography>
                                            </StyledAccordionSummary>
                                            <StyledAccordionDetails>
                                                <Typography
                                                    sx={{
                                                        fontSize: { xs: '0.95rem', md: '1rem' },
                                                        whiteSpace: 'pre-wrap'
                                                    }}
                                                    dangerouslySetInnerHTML={{ __html: answer }}
                                                />
                                            </StyledAccordionDetails>
                                        </StyledAccordion>
                                    </motion.div>
                                );
                            })
                        ) : (
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    py: 8,
                                    color: theme.palette.text.secondary
                                }}
                            >
                                <HelpOutline sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                                <Typography variant="h6">
                                    No questions found matching your search
                                </Typography>
                            </Box>
                        )}
                    </Box>
                </motion.div>
            </Container>
        </Box>
    );
};

export default ModernFAQ;
